import { OpenShopOrdersOfSourceButton } from "src/components/views/erp/utilComponents/OpenShopOrdersOfSourceButton.tsx";
import { OpenPurchaseOrderButton } from "src/components/views/erp/utilComponents/OpenPurchaseOrderButton.tsx";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import i18n from "i18next";
import {
	faBan,
	faBoxOpen,
	faCalendarAlt,
	faCheckCircle,
	faClone,
	faFileDownload,
	faPlus,
	faShare,
	faSignInAlt,
	faTools,
	faUndo,
	faVial,
} from "@fortawesome/pro-regular-svg-icons";
import { CustomerOrderLineConfiguratorView } from "src/components/views/erp/configurator/configuratorForm/impl/CustomerOrderLineConfiguratorView.tsx";
import { downloadFile } from "src/utils/fileDownloading.ts";
import { AavoDataGridRowContextMenuParams } from "src/components/common/dataGrid/types";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { dayJsToDateIsoString } from "src/utils/dayjsUtils.ts";
import dayjs from "dayjs";
import { CustomerOrderLineActionApi } from "src/api/generated/erp/sales/customerOrder/api/customerOrderLineActionApi.ts";
import { OpenObjectChangeLogButton } from "src/components/views/changeLogging/OpenObjectChangeLogButton.tsx";
import { CustomerOrderNoAcquisitionLineDeliveryForm } from "src/components/views/erp/sales/customerOrder/CustomerOrderNoAcquisitionLineDeliveryForm.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { useMessageDialog } from "src/components/common/dialogs/messageDialog/MessageDialogContext.tsx";
import { OpenPartConfigurationButton } from "../../configurator/inspecting/OpenPartConfigurationButton";
import { OpenConfigurationHistoryButton } from "src/components/views/erp/configurator/objectConfigurationHistory/OpenConfigurationHistoryButton.tsx";
import { ShopOrderPrintApi } from "src/api/generated/erp/production/api/shopOrder/shopOrderPrintApi.ts";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { CustomerOrderLineForm } from "src/components/views/erp/sales/customerOrderLine/CustomerOrderLineForm.tsx";
import { OpenInventoryProfileButton } from "../../utilComponents/OpenInventoryProfileButton.tsx";
import { CustomerOrder } from "src/api/generated/erp/db/types/tables/customerOrder.ts";
import { createCustomerOrderLineConfiguratorType } from "src/components/views/erp/configurator/configuratorForm/utils/partConfiguratorTypeCreators.ts";
import { getConfiguratorFormDialogTitle } from "src/components/views/erp/configurator/configuratorForm/utils/configuratorFormUtils.tsx";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { CustomerOrderLineDataGridApi_CustomerOrderLineDto } from "src/api/generated/erp/sales/customerOrder/api/customerOrderLineDataGridApi.ts";
import { Divider } from "@mui/material";
import { OpenSitePartButton } from "src/components/views/erp/utilComponents/OpenSitePartButton.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { RealObjectTestConfiguratorRootRevisionDialog } from "src/components/views/erp/configurator/configuratorTesting/RealObjectTestConfiguratorRootRevisionDialog.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { CustomUiActionObjectMenuItems } from "src/components/common/customUiActions/CustomUiActionMenuItems.tsx";

export interface CustomerOrderLinesDataGridContextMenuComponentsProps extends AavoDataGridRowContextMenuParams<CustomerOrderLineDataGridApi_CustomerOrderLineDto> {
	refreshData: () => Promise<unknown>;
	parentLineId: number | null;
	customerOrder: CustomerOrder;
	showReleaseButton: boolean;
	editable: boolean;
}

export const CustomerOrderLinesDataGridContextMenuComponents = ({
	refreshData,
	parentLineId,
	customerOrder,
	showReleaseButton,
	editable,
	...props
}: CustomerOrderLinesDataGridContextMenuComponentsProps) => {
	const { row, onlySingleRowSelected, allSelectedRows, multipleRowsSelected } = props;
	const { openDialog } = useGenericDialog();
	const showInputDialog = useInputDialog();
	const showConfirmDialog = useConfirmDialog();
	const showMessageDialog = useMessageDialog();
	const hasPermission = useHasPermission();

	const lineCanBeDeliveredDirectly =
		row.acquisitionMethod === "NO_ACQUISITION" && row.salesPartType !== "PACKAGE_" && !row.automaticDelivery;

	const configuratorReadonly =
		!editable ||
		row.customerOrderLineState !== "INITIAL" ||
		(row.acquisitionMethod === "SPLIT_BOM" && row.childLines.length > 0);
	const selectedRowsCanBeCancelled = allSelectedRows.every((selectedRow) => selectedRow.childLines.length === 0);
	const selectedRowsCanRevertRelease = allSelectedRows.every(
		(selectedRow) =>
			["RELEASED", "RESERVED"].includes(selectedRow.customerOrderLineState) &&
			!selectedRow.markedToFullyDelivered,
	);
	const canMarkAsFullyDelivered =
		editable &&
		onlySingleRowSelected &&
		!row.markedToFullyDelivered &&
		!["INITIAL", "CANCELLED"].includes(row.customerOrderLineState) &&
		row.deliveredQuantity < row.salesQuantityAsWarehouseUnits;
	const canRevertMarkAsFullyDelivered =
		editable &&
		onlySingleRowSelected &&
		row.markedToFullyDelivered &&
		!["INVOICED", "CANCELLED"].includes(row.customerOrderLineState);
	const canUnpackConfiguration =
		editable &&
		onlySingleRowSelected &&
		row.acquisitionMethod === "SPLIT_BOM" &&
		row.customerOrderLineState === "INITIAL" &&
		row.partConfigurationId != null &&
		row.configurationUnpackedAt == null &&
		row.childLines.length === 0;
	const canRevertConfigurationUnpacking =
		editable &&
		onlySingleRowSelected &&
		row.acquisitionMethod === "SPLIT_BOM" &&
		row.customerOrderLineState === "INITIAL" &&
		row.partConfigurationId != null &&
		row.childLines.length > 0 &&
		row.childLines.every((r) => r.customerOrderLineState === "INITIAL");

	return [
		<CustomUiActionObjectMenuItems
			key={"customFunctionButtons"}
			view={"CUSTOMER_ORDER_LINES"}
			objectIds={allSelectedRows.map((line) => line.customerOrderLineId)}
		/>,
		editable && showReleaseButton && row.customerOrderLineState === "INITIAL" && (
			<AsyncMenuButton
				icon={faShare}
				key={"release"}
				label={i18n.t("release")}
				onClick={async () => {
					const confirmed = await showConfirmDialog({
						title: i18n.t("are_you_sure"),
						message: i18n.t("release_customer_order_lines_confirmation"),
					});
					if (!confirmed) return;

					await CustomerOrderLineActionApi.releaseCustomerOrderLines({
						customerOrderLineIds: allSelectedRows.map((row) => row.customerOrderLineId),
					});
					await refreshData();
				}}
				disabled={
					customerOrder.capacityReservation ? i18n.t("customer_order_has_capacity_reservation") : undefined
				}
			/>
		),
		editable && allSelectedRows.length > 0 && selectedRowsCanRevertRelease && (
			<AsyncMenuButton
				icon={faUndo}
				key={"revertRelease"}
				label={i18n.t("revert_release")}
				onClick={async () => {
					const confirmed = await showConfirmDialog({
						title: i18n.t("are_you_sure"),
						message: i18n.t("revert_customer_order_line_release_confirmation"),
					});
					if (!confirmed) return;

					await CustomerOrderLineActionApi.revertReleaseCustomerOrderLines({
						customerOrderLineIds: allSelectedRows.map((selectedRow) => selectedRow.customerOrderLineId),
					});
					await refreshData();
				}}
			/>
		),
		editable && allSelectedRows.length > 0 && selectedRowsCanBeCancelled && (
			<AsyncMenuButton
				key={"cancelButton"}
				label={
					multipleRowsSelected ? i18n.t("cancel_selected") : i18n.t("cancel_customer_order_line")
				}
				icon={faBan}
				onClick={async () => {
					if (!row) return;
					const confirmed = await showConfirmDialog({
						message: i18n.t("confirm_cancel_customer_order_lines"),
						cancelButtonText: i18n.t("no"),
					});
					if (!confirmed) return;

					await CustomerOrderLineActionApi.cancelCustomerOrderLines({
						customerOrderLineIds: allSelectedRows.map((selectedRow) => selectedRow.customerOrderLineId),
					});
					await refreshData();
				}}
			/>
		),
		editable && onlySingleRowSelected && (
			<AsyncMenuButton
				key={"clone"}
				label={i18n.t("copy")}
				icon={faClone}
				onClick={async () => {
					openFormOnDialog({
						openDialog,
						component: CustomerOrderLineForm,
						size: "xl",
						title: i18n.t("copy_customer_order_line"),
						agentContext: {
							key: defineAgentUiContextKey("erp.sales.customerOrderLine.copyFormDialog"),
							data: {
								customerOrderId: row.customerOrderId,
								copySourceCustomerOrderLineId: row.customerOrderLineId,
								parentCustomerOrderLineId: parentLineId,
							},
						},
						props: {
							customerOrderId: row.customerOrderId,
							customerOrderLineId: undefined,
							parentLineId: parentLineId ?? undefined,
							copySourceCustomerOrderLine: row,
						},
						onSubmit: refreshData,
					});
				}}
			/>
		),
		editable && lineCanBeDeliveredDirectly && row.deliveredQuantity !== row.salesQuantityAsWarehouseUnits && (
			<AsyncMenuButton
				key={"directDeliveryButton"}
				label={i18n.t("deliver")}
				icon={faSignInAlt}
				onClick={async () => {
					openDialog(({ closeDialog }) => ({
						title: i18n.t("quantity_to_deliver"),
						size: "sm",
						agentContext: {
							key: defineAgentUiContextKey("erp.sales.customerOrderLine.directDeliveryDialog"),
							pathSegments: [i18n.t("deliver")],
							data: {
								customerOrderId: row.customerOrderId,
								customerOrderLineId: row.customerOrderLineId,
								customerOrderLineIds: allSelectedRows.map((selectedRow) => selectedRow.customerOrderLineId),
							},
						},
						content: (
							<CustomerOrderNoAcquisitionLineDeliveryForm
								customerOrderLines={allSelectedRows}
								onCompleted={async () => {
									await refreshData();
									await closeDialog();
								}}
								isRevert={false}
							/>
						),
					}));
				}}
			/>
		),
		editable && lineCanBeDeliveredDirectly && row.deliveredQuantity != 0 && (
			<AsyncMenuButton
				key={"revertServiceLineDeliverButton"}
				label={i18n.t("revert_delivery")}
				icon={faUndo}
				onClick={async () => {
					openDialog(({ closeDialog }) => ({
						title: i18n.t("quantity_to_revert"),
						size: "sm",
						agentContext: {
							key: defineAgentUiContextKey("erp.sales.customerOrderLine.revertDirectDeliveryDialog"),
							pathSegments: [i18n.t("revert_delivery")],
							data: {
								customerOrderId: row.customerOrderId,
								customerOrderLineId: row.customerOrderLineId,
								customerOrderLineIds: allSelectedRows.map((selectedRow) => selectedRow.customerOrderLineId),
							},
						},
						content: (
							<CustomerOrderNoAcquisitionLineDeliveryForm
								customerOrderLines={allSelectedRows}
								onCompleted={async () => {
									await refreshData();
									await closeDialog();
								}}
								isRevert={true}
							/>
						),
					}));
				}}
			/>
		),
		editable && parentLineId == null && allSelectedRows.length > 0 && (
			<AsyncMenuButton
				key={"changePlannedDeliveryDateButton"}
				label={i18n.t("change_planned_delivery_date")}
				icon={faCalendarAlt}
				onClick={async () => {
					await changeCustomerOrderLinePlannedDeliveryDate();
					await refreshData();
				}}
			/>
		),

		canMarkAsFullyDelivered && (
				<AsyncMenuButton
					key={"markToFullyDeliveredButton"}
					label={i18n.t("mark_as_fully_delivered")}
					icon={faCheckCircle}
					onClick={async () => {
						const confirmed = await showConfirmDialog({
							title: i18n.t("are_you_sure"),
							message: i18n.t("mark_customer_order_line_as_fully_delivered_confirmation"),
						});
						if (!confirmed) return;

						await CustomerOrderLineActionApi.markCustomerOrderLineToFullyDelivered({
							customerOrderLineId: row.customerOrderLineId,
						});
						await refreshData();
					}}
				/>
			),
		canRevertMarkAsFullyDelivered && (
				<AsyncMenuButton
					key={"revertMarkToFullyDeliveredButton"}
					label={i18n.t("revert_mark_as_fully_delivered")}
					icon={faUndo}
					onClick={async () => {
						const confirmed = await showConfirmDialog({
							title: i18n.t("are_you_sure"),
							message: i18n.t("revert_mark_customer_order_line_as_fully_delivered_confirmation"),
						});
						if (!confirmed) return;

						await CustomerOrderLineActionApi.revertMarkCustomerOrderLineToFullyDelivered({
							customerOrderLineId: row.customerOrderLineId,
						});
						await refreshData();
					}}
				/>
			),
		row.partIsConfigurable && [
			<Divider key={"divider1"} />,
			onlySingleRowSelected && (
				<AsyncMenuButton
					key={"configureButton"}
					label={configuratorReadonly ? i18n.t("configure_read_only") : i18n.t("configure")}
					icon={faTools}
					onClick={async () => {
						const configuratorType = createCustomerOrderLineConfiguratorType(row.customerOrderLineId);
						const dialogTitle = await getConfiguratorFormDialogTitle({
							configuratorType: configuratorType,
						});

						openDialog(({ closeDialog, onDataDirtyStateChanged }) => ({
							title: dialogTitle,
							agentContext: {
								key: defineAgentUiContextKey("erp.sales.customerOrderLineConfigurator.dialog"),
								pathSegments: [`${i18n.t("customer_order_line")} ${row.customerOrderLineId}`],
								data: {
									customerOrderId: row.customerOrderId,
									customerOrderLineId: row.customerOrderLineId,
									partConfigurationId: row.partConfigurationId ?? null,
								},
							},
							content: (
								<CustomerOrderLineConfiguratorView
									configuratorType={configuratorType}
									customerOrderId={row.customerOrderId}
									currentPartConfigurationId={row.partConfigurationId}
									closeDialog={closeDialog}
									refreshSourceView={refreshData}
									disabled={configuratorReadonly}
									onDataDirtyStateChanged={onDataDirtyStateChanged}
								/>
							),
						}));
					}}
				/>
			),
			onlySingleRowSelected && hasPermission(Permissions.Parts.Configurators.TEST) && (
				<AsyncMenuButton
					key={"testConfigureButton"}
					label={i18n.t("test_configuration")}
					icon={faVial}
					onClick={openTestConfigurator}
				/>
			),
			onlySingleRowSelected && (
				<OpenPartConfigurationButton
					key={"openConfigurationsButton"}
					partConfigurationId={row.partConfigurationId}
					partConfiguratorType={{
						type: "customerOrderLine",
						customerOrderLineId: row.customerOrderLineId,
						explicitPartConfigurationId: null,
						testContextEnabled: false,
					}}
					vatPercent={row.vatPercent}
				/>
			),
			canUnpackConfiguration && (
					<AsyncMenuButton
						icon={faBoxOpen}
						key={"unpackConfiguration"}
						label={i18n.t("unpack_configuration")}
						onClick={async () => {
							const confirmed = await showConfirmDialog({
								title: i18n.t("are_you_sure"),
								message: i18n.t("unpack_configuration") + "?",
							});
							if (!confirmed) return;

							await CustomerOrderLineActionApi.unpackCustomerOrderLineConfiguration({
								customerOrderLineId: row.customerOrderLineId,
							});
							await refreshData();
						}}
					/>
				),
			canRevertConfigurationUnpacking && (
					<AsyncMenuButton
						icon={faUndo}
						key={"revertUnpackConfiguration"}
						label={i18n.t("revert_configuration_unpacking")}
						onClick={async () => {
							const confirmed = await showConfirmDialog({
								message: i18n.t("revert_configuration_unpacking_confirmation"),
							});
							if (!confirmed) return;

							await CustomerOrderLineActionApi.revertUnpackCustomerOrderLineConfiguration({
								customerOrderLineId: row.customerOrderLineId,
							});
							showMessageDialog({
								title: i18n.t("warning"),
								content: i18n.t("check_main_line_values_after_reverting_configuration_unpacking"),
							});
							await refreshData();
						}}
					/>
				),
			onlySingleRowSelected && row.partConfigurationId != null && (
				<OpenConfigurationHistoryButton
					key={"openConfigurationHistoryButton"}
					objectType={"CUSTOMER_ORDER_LINE"}
					objectId={row.customerOrderLineId}
					refreshSourceView={refreshData}
				/>
			),
		],
		editable && onlySingleRowSelected && row.salesPartType == "PACKAGE_" && [
				<Divider key={"divider2"} />,
				<AsyncMenuButton
					key={"addContentLine"}
					icon={faPlus}
					label={i18n.t("add_child_line")}
					onClick={() => {
						openFormOnDialog({
							openDialog,
							component: CustomerOrderLineForm,
							size: "lg",
							title: i18n.t("customer_order_line"),
							agentContext: {
								key: defineAgentUiContextKey("erp.sales.customerOrderChildLine.formDialog"),
								data: {
									customerOrderId: row.customerOrderId,
									parentCustomerOrderLineId: row.customerOrderLineId,
								},
							},
							props: {
								customerOrderId: row.customerOrderId,
								customerOrderLineId: undefined,
								parentLineId: row.customerOrderLineId,
							},
							onSubmit: refreshData,
						});
					}}
				/>,
			],
		<Divider key={"divider3"} />,
		onlySingleRowSelected && (
			<DocumentsOfObjectButton
				key={"documents"}
				variant={"menu"}
				label={i18n.t("documents")}
				objectRef={{ objectType: "CUSTOMER_ORDER_LINE", objectId: row.customerOrderLineId }}
			/>
		),
		onlySingleRowSelected && row.releaseDate != null && row.acquisitionMethod === "MANUFACTURE" && (
			<OpenShopOrdersOfSourceButton
				key={"openShopOrdersButton"}
				shopOrderSource={{
					sourceType: "CUSTOMER_ORDER_LINE",
					sourceId: row.customerOrderLineId,
				}}
			/>
		),
		onlySingleRowSelected && (
			<OpenPurchaseOrderButton key={"openPurchaseOrderButton"} purchaseOrderId={row.purchaseOrderId} />
		),
		onlySingleRowSelected && <OpenSitePartButton partId={row.partId} key={"part"} />,
		onlySingleRowSelected && row.shopOrderId != null && (
			<AsyncMenuButton
				key={"printShopOrderSurveysButton"}
				label={i18n.t("print_shop_order_surveys")}
				icon={faFileDownload}
				onClick={async () => {
					const fileHandle = await ShopOrderPrintApi.printSurveysOfShopOrderOperations({
						shopOrderId: row.shopOrderId!,
					});
					downloadFile(fileHandle);
				}}
			/>
		),
		onlySingleRowSelected && <OpenInventoryProfileButton key={"inventoryLevelProfile"} partId={row.partId} />,
		onlySingleRowSelected && (
			<OpenObjectChangeLogButton
				key={"changeLog"}
				objectRef={{
					objectType: "CUSTOMER_ORDER_LINE",
					objectId: row.customerOrderLineId,
				}}
			/>
		),
	];

	async function openTestConfigurator() {
		const configuratorType = createCustomerOrderLineConfiguratorType(row.customerOrderLineId, true);
		openDialog(({ closeDialog }) => ({
			title: i18n.t("root_revision_to_use"),
			size: "sm",
			agentContext: {
				key: defineAgentUiContextKey("erp.sales.customerOrderLineTestConfiguratorRootRevision.dialog"),
				pathSegments: [i18n.t("test_configuration")],
				data: {
					customerOrderId: row.customerOrderId,
					customerOrderLineId: row.customerOrderLineId,
					partConfigurationId: row.partConfigurationId ?? null,
				},
			},
			content: (
				<RealObjectTestConfiguratorRootRevisionDialog
					configuratorType={configuratorType}
					closeDialog={closeDialog}
					onSubmit={async () => {
						const dialogTitle = await getConfiguratorFormDialogTitle({
							configuratorType: configuratorType,
						});

						openDialog(({ closeDialog, onDataDirtyStateChanged }) => ({
							title: dialogTitle,
							agentContext: {
								key: defineAgentUiContextKey("erp.sales.customerOrderLineTestConfigurator.dialog"),
								pathSegments: [`${i18n.t("customer_order_line")} ${row.customerOrderLineId}`],
								data: {
									customerOrderId: row.customerOrderId,
									customerOrderLineId: row.customerOrderLineId,
									partConfigurationId: row.partConfigurationId ?? null,
								},
							},
							content: (
								<CustomerOrderLineConfiguratorView
									configuratorType={configuratorType}
									customerOrderId={row.customerOrderId}
									currentPartConfigurationId={row.partConfigurationId}
									closeDialog={closeDialog}
									refreshSourceView={refreshData}
									disabled={configuratorReadonly}
									onDataDirtyStateChanged={onDataDirtyStateChanged}
								/>
							),
						}));
					}}
				/>
			),
		}));
	}

	async function changeCustomerOrderLinePlannedDeliveryDate() {
		const newPlannedDeliveryDate = await askDeliveryDate();
		const updateAcquisitionObjectsDates =
			row.customerOrderLineState === "INITIAL" ? true : await askAcquisitionObjectUpdate();
		if (newPlannedDeliveryDate == null || updateAcquisitionObjectsDates == null) return;

		await CustomerOrderLineActionApi.rescheduleSelectedCustomerOrderLines({
			customerOrderLineIds: allSelectedRows.map((x) => x.customerOrderLineId),
			newPlannedDeliveryDate: dayJsToDateIsoString(newPlannedDeliveryDate),
			updateAcquisitionObjects: updateAcquisitionObjectsDates,
		});

		function askDeliveryDate() {
			return showInputDialog({
				defaultValue: dayjs(row.plannedDeliveryDate),
				type: "date",
				title: i18n.t("change_planned_delivery_date"),
				fieldLabel: i18n.t("new_delivery_date"),
				required: true,
			});
		}

		function askAcquisitionObjectUpdate() {
			return showInputDialog({
				defaultValue: true,
				type: "boolean",
				title: i18n.t("change_planned_delivery_date"),
				fieldLabel: i18n.t("update_also_initial_purchase_and_shop_orders_dates"),
			});
		}
	}
};
